import { useRef, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";

import type { UiTheme } from "../theme";
import { ZoomControls } from "./ZoomControls";
import { ZOOM_MIN, clampZoom, type ZoomPanViewProps } from "./zoomPanModel";

// Web half of the zoom (native: ZoomPanView.tsx): the ZoomControls buttons,
// no pinch. The content is redrawn wider rather than scaled, so clicks hit
// the real shapes; the box scrolls sideways and the page scrolls down.
export function ZoomPanView({ theme, children }: ZoomPanViewProps) {
  const styles = makeStyles(theme);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const scrollRef = useRef<ScrollView>(null);
  const scrollX = useRef(0);

  const zoomTo = (next: number) => {
    const z = clampZoom(next);
    setZoom(z);
    if (width === undefined) return;
    // Keep the centre of the view where it was.
    const centre = (scrollX.current + width / 2) / (width * zoom);
    requestAnimationFrame(() =>
      scrollRef.current?.scrollTo({ x: centre * width * z - width / 2, animated: false }),
    );
  };

  const onLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    setWidth((prev) => (prev === w ? prev : w));
  };
  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollX.current = e.nativeEvent.contentOffset.x;
  };

  return (
    <View style={styles.root}>
      <View onLayout={onLayout}>
        <ScrollView
          ref={scrollRef}
          horizontal
          scrollEventThrottle={16}
          onScroll={onScroll}
          contentContainerStyle={width === undefined ? undefined : { width: width * zoom }}
        >
          {children(width === undefined ? undefined : width * zoom)}
        </ScrollView>
      </View>
      <ZoomControls theme={theme} zoom={zoom} onZoom={zoomTo} />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    root: { alignSelf: "stretch", gap: s.s2 },
  });
}
